import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from '../services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuthStatus = async () => {
      const storedToken = localStorage.getItem('token');
      
      if (storedToken) {
        try {
          setToken(storedToken);
          // Set default auth header for all requests
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          
          // Fetch current user data
          const response = await axios.get('/api/auth/me');
          setCurrentUser(response.data.user);
        } catch (err) {
          console.error('Auth validation error:', err);
          // If token is invalid, clear it
          localStorage.removeItem('token');
          setToken(null);
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      
      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  const login = async (email, password, role) => {
    try {
      setError(null);
      const response = await axios.post('/api/auth/login', { email, password, role });
      
      // Store token
      const authToken = response.data.session.access_token;
      localStorage.setItem('token', authToken);
      setToken(authToken);
      
      // Set auth header
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      
      // Set user data
      setCurrentUser(response.data.user);
      
      return response.data.user;
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
      throw err;
    }
  };

  // Admin-only registration
  const register = async (userData) => {
    try {
      setError(null);
      const response = await axios.post('/api/auth/register', userData);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
      throw err;
    }
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Always clear local state even if API call fails
      localStorage.removeItem('token');
      setToken(null);
      delete axios.defaults.headers.common['Authorization'];
      setCurrentUser(null);
    }
  };

  const resetPassword = async (email) => {
    try {
      setError(null);
      const response = await axios.post('/api/auth/reset-password', { email });
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Password reset failed');
      throw err;
    }
  };

  const value = {
    currentUser,
    user: currentUser, // Alias for compatibility
    token,
    loading,
    error,
    login,
    register,
    logout,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};